import { useState, type ChangeEvent } from 'react';
import { AlertTriangle, BadgeCheck, Phone, UserPlus } from 'lucide-react';
import { ChampRecherchable } from '../ui/ChampRecherchable';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur, Pastille } from '../ui/Primitives';
import type { NouveauMaitreApprentissage } from '../../lib/useEntreprises';

/** Une fiche de maître d'apprentissage, telle que le contrôle du CERFA la rend. */
export interface MaitreApprentissageProposable {
  id: string;
  nom: string;
  prenom: string;
  emploiOccupe: string | null;
  telephone: string | null;
  email: string | null;
  diplomeLePlusEleve: string | null;
  niveauDiplome: string | null;
  anneesExperience: number | null;
  attestationEligibilite: boolean;
  /** Apprentis qu'il suit déjà sur la période du contrat, celui-ci exclu. */
  charge: {
    simultanes: number;
    alerte: string | null;
    contrats: { apprenti: string; jusquau: string }[];
  };
}

/**
 * Saisie minimale d'une nouvelle fiche : le reste se complète au CERFA. La même
 * que depuis la fiche entreprise (« Ajouter un contact »).
 */
export type SaisieNouveauMaitre = NouveauMaitreApprentissage;

/** `AAAA-MM-JJ` → `JJ/MM/AAAA`, sans passer par un instant ni un fuseau. */
function jourLisible(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  return `${jour}/${mois}/${annee}`;
}

/** « suit 2 apprentis », « aucun apprenti suivi ». */
function charge(nombre: number): string {
  return nombre === 0
    ? 'aucun apprenti suivi'
    : `suit ${nombre} apprenti${nombre > 1 ? 's' : ''}`;
}

/**
 * Choix du maître d'apprentissage du contrat, en tête de son bloc.
 *
 * La liste est celle des fiches de l'**employeur du contrat** : un maître
 * d'apprentissage appartient à son entreprise. Choisir une fiche préremplit
 * les rubriques du CERFA, que le serveur relit depuis elle ; la personne qui
 * n'est pas encore mémorisée se crée ici, et se choisit ensuite comme les
 * autres. Le maître déjà au plafond de deux apprentis sur la période du
 * contrat est signalé — sans bloquer : la décision reste humaine.
 */
export function ChoixMaitreApprentissage({
  entreprise,
  maitres,
  maitreId,
  periode,
  onChoisir,
  onCreer,
  onCreationOuverte,
  desactive = false,
}: {
  entreprise: string;
  maitres: MaitreApprentissageProposable[];
  maitreId: string | null;
  periode: { debut: string; fin: string; depuisLeContrat: boolean };
  onChoisir: (id: string) => void;
  /** Crée la fiche et la retient ; lève l’erreur de l’API, affichée sur place. */
  onCreer: (saisie: SaisieNouveauMaitre) => Promise<void>;
  /**
   * Prévient quand la saisie d'une nouvelle fiche s'ouvre ou se ferme : les
   * rubriques du maître précédent ne doivent pas rester à côté, on y taperait
   * la nouvelle personne.
   */
  onCreationOuverte?: (ouverte: boolean) => void;
  desactive?: boolean;
}): JSX.Element {
  const [creation, setCreationLocale] = useState<SaisieNouveauMaitre | null>(null);
  const setCreation = (valeur: SaisieNouveauMaitre | null): void => {
    setCreationLocale(valeur);
    onCreationOuverte?.(valeur !== null);
  };
  const choisi = maitres.find((maitre) => maitre.id === maitreId) ?? null;

  const surPeriode = periode.depuisLeContrat
    ? periode.debut === periode.fin
      ? `au ${jourLisible(periode.debut)}`
      : `du ${jourLisible(periode.debut)} au ${jourLisible(periode.fin)}`
    : "aujourd'hui";

  return (
    <div className="space-y-3 rounded-xl border border-gray-100 bg-codialis-sand/40 p-3 sm:p-4 md:col-span-2">
      {creation ? (
        <FormulaireNouveauMaitre
          entreprise={entreprise}
          initiale={creation}
          onAnnuler={() => setCreation(null)}
          onEnregistrer={async (saisie) => {
            await onCreer(saisie);
            setCreation(null);
          }}
        />
      ) : (
        <>
          <ChampRecherchable
            id="cerfa-choix-maitre"
            libelle="Maître d’apprentissage"
            placeholder={
              maitres.length === 0 ? 'Aucune fiche mémorisée' : 'Nom, prénom ou fonction…'
            }
            options={maitres.map((maitre) => ({
              id: maitre.id,
              libelle: `${maitre.prenom} ${maitre.nom}`,
              detail: [maitre.emploiOccupe, charge(maitre.charge.simultanes)]
                .filter(Boolean)
                .join(' · '),
              complement: maitre.charge.alerte ? (
                <Pastille texte="2 apprentis ou plus" ton="warning" />
              ) : null,
            }))}
            valeur={maitreId}
            onChoisir={onChoisir}
            creation={{
              libelle: 'Créer une nouvelle fiche',
              onCreer: (recherche) => {
                // « Paul Técher » tapé dans la recherche : on ne le refait pas
                // taper. Le dernier mot est pris pour le nom.
                const mots = recherche.split(/\s+/).filter(Boolean);
                setCreation({
                  prenom: mots.length > 1 ? mots.slice(0, -1).join(' ') : '',
                  nom: mots.at(-1) ?? '',
                });
              },
            }}
            aucunResultat={`Aucun maître d’apprentissage de ${entreprise} ne correspond.`}
            desactive={desactive}
            invalide={!choisi}
          />

          {!choisi ? (
            <p className="text-xs font-semibold text-red-700">
              {maitres.length === 0
                ? `Aucun maître d’apprentissage n’est mémorisé chez ${entreprise} : créez sa fiche.`
                : 'Choisissez le maître d’apprentissage du contrat, ou créez sa fiche.'}
            </p>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-codialis-gray">
                {choisi.telephone ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5" aria-hidden />
                    {choisi.telephone}
                  </span>
                ) : null}
                {choisi.anneesExperience !== null ? (
                  <span>
                    {choisi.anneesExperience} an{choisi.anneesExperience > 1 ? 's' : ''} d’expérience
                  </span>
                ) : null}
                {choisi.attestationEligibilite ? (
                  <span className="inline-flex items-center gap-1.5 text-codialis-navy">
                    <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
                    Éligibilité attestée
                  </span>
                ) : null}
                <span>
                  {choisi.charge.simultanes === 0
                    ? `Aucun autre apprenti suivi ${surPeriode}`
                    : `Suit déjà ${choisi.charge.simultanes} apprenti${choisi.charge.simultanes > 1 ? 's' : ''} ${surPeriode}`}
                </span>
              </div>

              {choisi.charge.alerte ? (
                <div
                  role="alert"
                  className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                >
                  <p className="flex items-start gap-2 font-semibold">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    {choisi.charge.alerte}
                  </p>
                  <ul className="mt-1.5 space-y-0.5 pl-6 text-xs">
                    {choisi.charge.contrats.map((contrat) => (
                      <li key={`${contrat.apprenti}-${contrat.jusquau}`}>
                        {contrat.apprenti} — suivi jusqu’au {jourLisible(contrat.jusquau)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Nouvelle fiche, saisie sur place. Seule l'identité est exigée : la date de
 * naissance, le diplôme et l'attestation se complètent ensuite dans les
 * rubriques du CERFA, comme pour une fiche déjà connue.
 */
function FormulaireNouveauMaitre({
  entreprise,
  initiale,
  onAnnuler,
  onEnregistrer,
}: {
  entreprise: string;
  initiale: SaisieNouveauMaitre;
  onAnnuler: () => void;
  onEnregistrer: (saisie: SaisieNouveauMaitre) => Promise<void>;
}): JSX.Element {
  const [saisie, setSaisie] = useState({
    nom: initiale.nom,
    prenom: initiale.prenom,
    emploiOccupe: '',
    telephone: '',
    email: '',
    anneesExperience: '',
  });
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const incomplet = !saisie.nom.trim() || !saisie.prenom.trim();

  const champ =
    'mt-1 min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-codialis-navy outline-none transition focus:border-codialis-sky focus:ring-4 focus:ring-sky-100';
  const modifier =
    (cle: keyof typeof saisie) =>
    (evenement: ChangeEvent<HTMLInputElement>): void =>
      setSaisie((precedente) => ({ ...precedente, [cle]: evenement.target.value }));

  const enregistrer = async (): Promise<void> => {
    setEnvoi(true);
    setErreur(null);
    try {
      const experience = saisie.anneesExperience.trim();
      await onEnregistrer({
        nom: saisie.nom.trim(),
        prenom: saisie.prenom.trim(),
        ...(saisie.emploiOccupe.trim() ? { emploiOccupe: saisie.emploiOccupe.trim() } : {}),
        ...(saisie.telephone.trim() ? { telephone: saisie.telephone.trim() } : {}),
        ...(saisie.email.trim() ? { email: saisie.email.trim() } : {}),
        ...(experience ? { anneesExperience: Number(experience) } : {}),
      });
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'La fiche n’a pas pu être créée.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <form
      className="space-y-3"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        if (!incomplet && !envoi) void enregistrer();
      }}
    >
      <div>
        <h4 className="flex items-center gap-2 text-sm font-bold text-codialis-navy">
          <UserPlus className="h-4 w-4" aria-hidden />
          Nouveau maître d’apprentissage
        </h4>
        <p className="mt-0.5 text-xs text-codialis-gray">
          La fiche est rattachée à {entreprise} et restera disponible pour ses prochains contrats.
        </p>
      </div>

      {erreur ? <MessageErreur message={erreur} /> : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="min-w-0 text-sm font-medium text-codialis-navy">
          Nom de naissance
          <input autoFocus value={saisie.nom} onChange={modifier('nom')} className={champ} />
        </label>
        <label className="min-w-0 text-sm font-medium text-codialis-navy">
          Prénom
          <input value={saisie.prenom} onChange={modifier('prenom')} className={champ} />
        </label>
        <label className="min-w-0 text-sm font-medium text-codialis-navy">
          Emploi occupé <span className="text-xs font-normal text-codialis-gray">facultatif</span>
          <input value={saisie.emploiOccupe} onChange={modifier('emploiOccupe')} className={champ} />
        </label>
        <label className="min-w-0 text-sm font-medium text-codialis-navy">
          Années d’expérience <span className="text-xs font-normal text-codialis-gray">facultatif</span>
          <input
            inputMode="numeric"
            value={saisie.anneesExperience}
            onChange={(evenement) =>
              setSaisie((precedente) => ({
                ...precedente,
                anneesExperience: evenement.target.value.replace(/\D/g, '').slice(0, 2),
              }))
            }
            className={champ}
          />
        </label>
        <label className="min-w-0 text-sm font-medium text-codialis-navy">
          Téléphone <span className="text-xs font-normal text-codialis-gray">facultatif</span>
          <input type="tel" value={saisie.telephone} onChange={modifier('telephone')} className={champ} />
        </label>
        <label className="min-w-0 text-sm font-medium text-codialis-navy">
          Courriel <span className="text-xs font-normal text-codialis-gray">facultatif</span>
          <input type="email" value={saisie.email} onChange={modifier('email')} className={champ} />
        </label>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <BoutonSecondaire onClick={onAnnuler} disabled={envoi}>
          Annuler
        </BoutonSecondaire>
        <BoutonPrincipal type="submit" disabled={incomplet || envoi}>
          {envoi ? 'Création…' : 'Créer la fiche'}
        </BoutonPrincipal>
      </div>
    </form>
  );
}
